/**
 * GAME NIGHT — desktop (migration 3039, docs/ROUNDS.md).
 * design/GT Round.dc.html — the desktop mirror's round page, and the
 * DESIGN-CHANGELOG entries 2026-08-25 → 2026-08-26 it carries. The mirror's
 * copy still says ROUND: Marcus renamed the feature in the app on 2026-08-28
 * and the prototype owes the same edit upstream (repo CLAUDE.md). Only the
 * words moved — files, screen key, RPCs and tables are all still `round`.
 *
 * Full screen: `DesktopApp` hides the top nav while `headerVM().isRound`, so
 * `◂ BACK TO THE BOARD` is the only way out — and it keeps your seat.
 *
 * Everything here is paint. `roundVM(s, s.serverNow())` is the whole model,
 * recomputed on every frame while the wheel moves (`useRoundClock`), so the
 * wheel's pose, the pin card and the CTA all read the SAME server instant.
 * Layout geometry comes from `roundLayout(w, h)` off the live viewport:
 * two columns at ≥1080 (no page scroll), stacked below it.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { Avatar } from '../../components/Avatar.tsx';
import { CoverArt } from '../../components/CoverArt.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { QuarterIcon } from '../../components/QuarterIcon.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { ArmedLabel, hueSplit, LeaderCrown, NominatorStack, QuarterSocket, useRoundClock, Wheel, WheelPointer } from '../../components/Wheel.tsx';
import { useQd } from '../../store/index.ts';
import { roundLayout, roundVM } from '../../store/selectors.round.ts';
import type { ArmPairVM, RoundVM } from '../../store/selectors.round.ts';
import FilterBar from '../components/FilterBar.tsx';
import { useSearchInputRef } from '../searchHotkey.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function useViewport(): { w: number; h: number } {
  const read = () =>
    typeof window === 'undefined' ? { w: 1280, h: 800 } : { w: window.innerWidth, h: window.innerHeight };
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const on = () => setVp(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vp;
}

/** The armed-control box: overflow hidden, relative, draining bar inside. */
function armBox(p: ArmPairVM, pad: string, fs: number): CSSProperties {
  return {
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    border: '1px solid ' + p.bd,
    background: p.bg,
    color: p.clr,
    fontFamily: MONO,
    fontSize: fs,
    letterSpacing: '.05em',
    padding: pad,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function Pips({ pips, size }: { pips: RoundVM['pips']; size: number }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size > 22 ? 4 : 3, height: size, flex: 'none' }}>
      {pips.map((q) =>
        q.on ? (
          <QuarterIcon key={q.key} hover={false} u={size / 14} sparkles={false} auto={false} />
        ) : (
          <QuarterSocket key={q.key} size={size} />
        ),
      )}
    </div>
  );
}

/** The pin card's art — its own component so `useArt` is one hook. */
function PinCard({ v, lay }: { v: RoundVM; lay: ReturnType<typeof roundLayout> }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(v.cardGoId + 'p', v.cardCands);
  return (
    <div
      onClick={() => {
        if (v.cardGoId) openGame(v.cardGoId);
      }}
      style={{
        cursor: v.cardCur,
        width: lay.cardW,
        flex: 'none',
        position: 'relative',
        aspectRatio: '2/3',
        border: v.cardBw + ' solid ' + v.cardBd,
        ...hueSplit(v.cardHues),
        background: v.cardOn ? v.cardFall : 'var(--gt-tile)',
        boxShadow: 'var(--gt-shadow-card)',
      }}
    >
      {v.cardOn ? (
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={24} ini={v.cardIni} hasCover={art.hasCover} cover={art.cover} />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--gt-field)',
          }}
        >
          <span style={{ fontFamily: PIXEL, fontSize: lay.qFs, color: 'var(--gt-ghost)', transform: 'translateY(0.13em)' }}>?</span>
        </div>
      )}
      {/* Everyone who backed it rides on the art, top-left, and the card is
          outlined in the FIRST nominator's hue (3054: the result card credits
          the whole stack — a game two people put a quarter on won because both
          of them did). */}
      {v.cardByOn ? (
        <div
          style={{
            position: 'absolute',
            left: 9,
            top: 9,
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            maxWidth: 'calc(100% - 18px)',
            background: 'var(--gt-ink)',
            border: '1px solid ' + v.cardBd,
            ...hueSplit(v.cardHues),
            padding: '5px 10px 5px 5px',
            boxShadow: 'var(--gt-shadow-btn)',
          }}
        >
          <NominatorStack bys={v.cardBys} size={22} fs={7} />
          <span
            style={{
              fontFamily: MONO,
              fontSize: 9.5,
              letterSpacing: '.06em',
              color: 'var(--gt-text-3)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {v.cardByTxt}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function Tile({ g }: { g: RoundVM['tiles'][number] }): JSX.Element {
  const rNom = useQd((s) => s.rNom);
  const art = useArt(g.id + 'p', g.coverCands);
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => {
        if (g.act) void rNom(g.id);
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      // 3px when a quarter is on it (Marcus, staging 2026-08-26), matching the pin card.
      style={{ display: 'flex', flexDirection: 'column', border: (g.heldN ? 3 : 1) + 'px solid ' + g.bd, ...hueSplit(g.bdHues), background: 'var(--gt-tile)', cursor: g.cur }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: g.fall, overflow: 'hidden' }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        {/* My coin, then every OTHER backer (3054) — one row, one tip, so a
            game three people are on says so in the corner it always used. */}
        {g.heldN ? (
          <div data-tip={g.tip} style={{ position: 'absolute', left: 5, top: 5, zIndex: 4, display: 'flex', alignItems: 'center', gap: g.coin && g.avs.length ? 4 : 0 }}>
            {g.coin ? <QuarterIcon hover={false} u={1.7} sparkles={false} auto={false} /> : null}
            <NominatorStack bys={g.avs} more={g.avMore} size={22} fs={8} />
          </div>
        ) : null}
        {g.chipOn ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--gt-ink)',
              borderTop: '1px solid ' + g.bd,
              ...hueSplit(g.bdHues),
              fontFamily: MONO,
              fontSize: 8.5,
              letterSpacing: '.06em',
              color: g.clr,
              padding: '5px 0',
              opacity: hov ? 1 : 0,
              transition: 'opacity .12s',
            }}
          >
            {g.lbl}
          </div>
        ) : null}
      </div>
      <div style={{ padding: '6px 7px', fontFamily: MONO, fontSize: 8.5, color: 'var(--gt-text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {g.nm}
      </div>
    </div>
  );
}

export default function Round(): JSX.Element | null {
  const s = useQd();
  const v = roundVM(s, s.serverNow());
  // The round page hides the nav and the tab bar, so this is the only box on
  // screen and therefore `/`'s target while it is up.
  const bqRef = useSearchInputRef();
  useRoundClock(s.round?.spin ?? null, s.serverNow);
  const { w, h } = useViewport();

  // The wheel, the rail and the pin card all paint covers, and a round's pool
  // is not the Discover head or the catalog head — the two sets
  // `revalidateVisible` covers — so nothing else asks `/api/steam/items` about
  // a nominated game (the note in `components/useArt.ts`).
  //
  // BEFORE the `if (!v)` below, like `useRoundClock` above it: a hook may not
  // sit after an early return. An idle round is `null` and queues nothing.
  useQueueArt(v ? [...v.wedges.map((w) => w.nom.id), ...v.tiles.map((t) => t.id), v.cardGoId] : []);

  if (!v) return null;
  const lay = roundLayout(w, h);
  const pixel8: CSSProperties = { fontFamily: PIXEL, fontSize: 8, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' };

  return (
    <div
      data-screen-label="Game night"
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        flex: 'none',
        height: lay.pageH,
        maxHeight: lay.pageH,
        overflow: lay.pageOv,
        boxSizing: 'border-box',
        background: 'var(--gt-bg)',
      }}
    >
      {/* header: navigation far left, round actions far right ------------ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '15px 24px',
          borderBottom: '1px solid var(--gt-line)',
          background: 'var(--gt-panel)',
          flexWrap: 'wrap',
        }}
      >
        <div
          onClick={() => s.rBack()}
          className="d-hov-acc"
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none' }}
        >
          {v.backTxt}
        </div>
        <div style={{ width: 1, height: 24, background: 'var(--gt-border-2)', flex: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, flex: 'none' }}>
          <QuarterIcon u={2} hover={false} />
          <span style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>GAME NIGHT</span>
        </div>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{v.rHeadTxt}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 'none', marginLeft: 'auto' }}>
          {v.rIn ? (
            <div onClick={() => void s.rLeave()} data-arm="leave" data-tip={v.rLvTip} style={armBox(v.lv, '9px 12px', 10)}>
              <ArmedLabel rest="✕ LEAVE GAME NIGHT" restV={v.lv.restV} armV={v.lv.armV} fill={v.lv.fill} anim={v.lv.anim} />
            </div>
          ) : null}
          {v.rEndOn ? (
            <div
              onClick={() => void s.rEnd()}
              data-arm="end"
              data-tip="CLOSES GAME NIGHT FOR THE PARTY · A NEW ONE CAN START RIGHT AFTER"
              style={armBox(v.en, '9px 12px', 10)}
            >
              <ArmedLabel rest="▮ END GAME NIGHT" restV={v.en.restV} armV={v.en.armV} fill={v.en.fill} anim={v.en.anim} />
            </div>
          ) : null}
        </div>
      </div>

      {/* not in the round --------------------------------------------------- */}
      {v.rOut ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--acc2Soft)', borderBottom: '1px solid var(--gt-line)', padding: '13px 24px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1, color: 'var(--acc2)' }}>✕</span>
            <span style={{ fontFamily: PIXEL, fontSize: 8, color: 'var(--acc2)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>YOU&apos;RE NOT IN GAME NIGHT</span>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-text-5)' }}>{v.rOutTxt}</span>
          <div style={{ flex: 1 }} />
          {v.rCanJoin ? <PixelButton label="JOIN GAME NIGHT ▸" fs={8} pad="11px 14px" nowrap fit lh="1" onClick={() => void s.rJoin()} /> : null}
          {v.rShutTxtOn ? <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>TONIGHT&apos;S DECIDED — NOTHING TO JOIN</span> : null}
        </div>
      ) : null}

      {/* body: stage + rail ------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: lay.shellDir, flex: 1, minHeight: 0, overflowY: lay.bodyOv }}>
        <div
          style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            padding: lay.bodyPad,
            minHeight: lay.stageMinH,
            overflowY: lay.stageOv,
          }}
        >
          {/* stage controls, top-right: everything that acts on the WHEEL */}
          <div style={{ position: 'absolute', top: 16, right: 20, zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {v.rUndoOn ? (
                <div
                  onClick={() => void s.rUndoSpin()}
                  data-arm="undo"
                  data-tip="CLEARS THE RESULT AND REOPENS NOMINATING · EVERY NOMINATION STAYS"
                  data-tip-pos="r"
                  style={armBox(v.ud, '9px 12px', 10)}
                >
                  <ArmedLabel rest="⟳ UNDO SPIN" restV={v.ud.restV} armV={v.ud.armV} fill={v.ud.fill} anim={v.ud.anim} />
                </div>
              ) : null}
              {v.rResetOn ? (
                <div
                  onClick={() => void s.rReset()}
                  data-arm="reset"
                  data-tip="CLEARS ALL NOMINATIONS AND SPINS · GAME NIGHT STAYS OPEN"
                  data-tip-pos="r"
                  style={armBox(v.rs, '9px 12px', 10)}
                >
                  <ArmedLabel rest="✕ RESET" restV={v.rs.restV} armV={v.rs.armV} fill={v.rs.fill} anim={v.rs.anim} />
                </div>
              ) : null}
            </div>
            {v.rHostOn ? (
              <div
                onClick={() => void s.rTglWt()}
                data-tip={v.wtTip}
                style={{
                  opacity: v.wtOp,
                  pointerEvents: v.wtPE,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '1px solid ' + v.wtBd,
                  background: v.wtBg,
                  color: v.wtClr,
                  fontFamily: MONO,
                  fontSize: 10,
                  letterSpacing: '.06em',
                  padding: '11px 13px',
                  cursor: v.wtCur,
                  whiteSpace: 'nowrap',
                }}
              >
                {v.wtLbl}
              </div>
            ) : null}
          </div>

          <PinCard v={v} lay={lay} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, flex: 'none' }}>
            <div style={{ position: 'relative', width: lay.wheelW, height: lay.wheelH, flex: 'none', cursor: 'default' }}>
              <div style={{ position: 'absolute', left: 0, top: 8 }}>
                <Wheel wedges={v.wedges} rot={v.rot} spin={s.round?.spin ?? null} serverNow={s.serverNow} px={lay.px} winId={v.winId} winHue={v.winHue} keyPrefix="d" onHov={s.rHovSet} onOpen={s.openGame} />
              </div>
              <div style={{ position: 'absolute', left: '50%', top: lay.hubTop, marginLeft: -17, marginTop: -17, zIndex: 3 }}>
                <QuarterIcon hover={false} u={2.4} sparkles={false} />
              </div>
              <WheelPointer style={{ position: 'absolute', left: '50%', top: 0, marginLeft: -13, zIndex: 4 }} />
            </div>
            {v.rHostOn ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', maxWidth: lay.wheelW }}>
                {v.needNoms ? (
                  <div style={{ maxWidth: 280, border: '1px dashed var(--gt-border-3)', padding: 12, textAlign: 'center', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', lineHeight: 1.9, color: 'var(--gt-muted-2)' }}>
                    NOTHING TO SPIN YET — NOBODY HAS NOMINATED
                  </div>
                ) : null}
                {v.canSpin || v.stSpinning ? (
                  <div style={{ display: 'flex', flex: 'none' }}>
                    <PixelButton label={v.spinLbl} fs={12} pad="20px 26px" nowrap fit lh="1" busy={v.stSpinning} onClick={() => void s.rSpin()} />
                  </div>
                ) : null}
                {v.stWin ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 'none' }}>
                    <PixelButton label="LOCK IT IN ▸" fs={11} pad="18px 24px" nowrap fit lh="1" onClick={() => void s.rLockIn()} />
                    <div
                      onClick={() => void s.rSpin()}
                      style={{ border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', padding: '9px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      ⟳ SPIN AGAIN — UNTIL YOU LOCK
                    </div>
                  </div>
                ) : null}
                {v.stLock ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 'none' }}>
                    <div style={{ border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: 13, fontFamily: MONO, fontSize: 9.5, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--acc)' }}>
                      ✓ LOCKED — TONIGHT: {v.winNm}
                    </div>
                    <div
                      onClick={() => void s.rUnlock()}
                      data-arm="unlock"
                      data-tip="PUTS THE PICK BACK IN PLAY · SPIN AGAIN, OR UNDO SPIN TO NOMINATE"
                      style={{ ...armBox(v.un, '9px 11px', 9), letterSpacing: '.06em' }}
                    >
                      <ArmedLabel rest="⟳ UNLOCK — PUT IT BACK IN PLAY" restV={v.un.restV} armV={v.un.armV} fill={v.un.fill} anim={v.un.anim} />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* right rail: WHO'S IN over NOMINATE --------------------------------- */}
        <div style={{ width: lay.railW, flex: 'none', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: lay.twoCol ? '100%' : 'none', overflow: 'hidden', borderLeft: lay.railBdL, borderTop: lay.railBdT, background: 'var(--gt-panel)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--gt-raised)', borderBottom: '1px solid var(--gt-border)', padding: '11px 14px', flex: 'none' }}>
            <span style={pixel8}>WHO&apos;S IN</span>
            <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{v.rosterTxt}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8, padding: 12, flex: 'none' }}>
            {v.roster.map((p) => (
              <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--gt-line)', background: 'var(--gt-field)', minWidth: 0 }}>
                <div style={{ position: 'relative', display: 'flex', flex: 'none' }}>
                  {p.host ? <LeaderCrown style={{ position: 'absolute', left: '50%', top: -8, marginLeft: -8, zIndex: 3 }} /> : null}
                  <Avatar size={24} fs={7} ini={p.ini} bg={p.bg} hasAv={p.hasAv} avBg={p.avBg} />
                </div>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', color: p.clr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{p.nm}</span>
                <Pips pips={p.pips} size={20} />
                {/* The leader's ✕ (2026-08-28), armed like every other
                    destructive control. `armBox`'s grid-area pattern holds the
                    card's width steady when ✕ becomes SURE?, so a 150px-min
                    grid cell never reflows its neighbours mid-decision. */}
                {p.kickOn ? (
                  <div
                    onClick={() => void s.rKick(p.key)}
                    data-arm={'kick:' + p.key}
                    data-tip={p.kickTip}
                    style={{ ...armBox(p.kick, '6px 8px', 9), flex: 'none' }}
                  >
                    <ArmedLabel rest="✕" restV={p.kick.restV} armV={p.kick.armV} fill={p.kick.fill} anim={p.kick.anim} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--gt-raised)', borderTop: '1px solid var(--gt-border)', borderBottom: '1px solid var(--gt-border)', padding: '11px 14px', flex: 'none' }}>
            <span style={pixel8}>NOMINATE</span>
            {/* Searching and filtering are both BROWSING, not nominating: they stay
                live in every shut state, which is why they sit in this strip rather
                than in the rows below that dim. */}
            <SearchBox
              value={v.bq}
              onChange={(e) => s.setBq('round', e.target.value)}
              onClear={() => s.setBq('round', '')}
              inputRef={bqRef}
              placeholder="SEARCH THE RAIL…"
              w="190px"
              pfs={10.5}
              ifs={10}
              pad="6px 10px"
            />
            <FilterBar />
            <div style={{ flex: 1 }} />
            {v.clrOn ? (
              <div onClick={() => void s.rClearMine()} style={{ border: '1px solid var(--acc2)', color: 'var(--acc2)', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', padding: '7px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {v.clrLbl}
              </div>
            ) : null}
            {v.shutOn ? <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{v.shutTxt}</span> : null}
          </div>
          <div style={{ opacity: v.nomOp, pointerEvents: v.nomPE, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--gt-line)', flex: 'none', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flex: 'none' }}>
              {v.tabs.map((t) => (
                <div
                  key={t.key}
                  onClick={() => s.rSetTier(t.key)}
                  style={{ marginLeft: t.ml, position: t.pos, zIndex: t.z, display: 'flex', alignItems: 'center', gap: 6, border: '1px solid ' + t.bd, background: t.bg, color: t.clr, fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', padding: '7px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {t.t}
                  <span style={{ color: t.nClr }}>{t.n}</span>
                </div>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--gt-border-2)', background: 'var(--gt-field)', padding: '6px 10px', height: 38, boxSizing: 'border-box', flex: 'none' }}>
              <Pips pips={v.pips} size={24} />
            </div>
          </div>
          <div style={{ opacity: v.nomOp, pointerEvents: v.nomPE, transition: 'opacity .14s', flex: lay.gridFlex, minHeight: 0, maxHeight: lay.gridMaxH, overflowY: lay.gridOv, padding: 12, display: 'flex', flexDirection: 'column', gap: 11 }}>
            {v.railEmpty ? (
              <div style={{ border: '1px dashed var(--gt-border-3)', padding: 18, textAlign: 'center', fontFamily: MONO, fontSize: 9.5, letterSpacing: '.06em', lineHeight: 1.9, color: 'var(--gt-muted)' }}>{v.railEmptyTxt}</div>
            ) : null}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(' + lay.tileMin + ',1fr))', gap: 10 }}>
              {v.tiles.map((g) => (
                <Tile key={g.key} g={g} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
